import { create } from 'zustand';

import { DEMO_IDENTITIES, persistDemoRole, toSessionRole, type DisplayRole } from './roles';
import { DEV_IMPERSONATION_TOKEN, type DevUser } from './dev-impersonation';

/**
 * Authenticated user, shaped to align with the OIDC/App ID claims that the real
 * flow will populate (SCRUM-7 six-role model). Today only `email` is real; the
 * rest are demo-derived until SCRUM-102 wires the token claims.
 */
export interface AuthUser {
  email: string;
  displayName: string;
  role: string;
  /** Branch for branch-scoped roles (Branch Manager / Teller) — demo-era. */
  branch?: string;
  /**
   * The authenticated tenant id (SCRUM-643). Set from the login flow's resolved
   * tenant. For authenticated /api/* calls the SERVER derives the tenant from the
   * validated token's tenant_id claim (TenantResolutionMiddleware, SCRUM-102), so
   * this is NOT sent as a header on those calls. It exists for the few surfaces
   * that cannot carry the Bearer — notably the native EventSource SSE stream,
   * which passes it as a tenantId query param (client-supplied, same demo-era
   * trust as before; real EventSource auth is a separate follow-up).
   */
  tenantId?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  /**
   * The App ID access token from the Custom Identity exchange (SCRUM-448), attached as
   * `Authorization: Bearer` on `/api/*` requests by the shared apiFetch client. Held in
   * MEMORY ONLY (never persisted — a refresh returns to the login screen, matching the
   * store's overall no-persist posture and keeping the token off disk). Null in the demo-era
   * path (no real exchange) and until SCRUM-431 configures the live App ID exchange, in which
   * case apiFetch simply sends no Authorization header (the server treats it as unauthenticated).
   */
  accessToken: string | null;
  isLoading: boolean;
  error: string | null;
  /**
   * Sign-in from the login form. Real path (SCRUM-573): resolve the tenant from
   * the email domain, then redirect to that tenant's bank IdP (the password is
   * entered at the IdP, not here — the arg is used only by the demo shortcut).
   */
  // password is optional/ignored: the email-first flow enters it at the bank IdP
  // (SCRUM-576 removed the demo password path). Retained for callers/tests.
  signIn: (email: string, password?: string) => Promise<void>;
  /**
   * SSO-button sign-in. Real path resolves the tenant from the supplied email
   * and redirects to its IdP (SCRUM-573); DEMO shortcut in dev/E2E builds ignores
   * the email.
   */
  signInWithEntra: (email: string) => Promise<void>;
  /** Completes the SSO flow on /auth/callback: server-side code exchange (SCRUM-102). */
  completeSsoCallback: (callbackUrl: string) => Promise<void>;
  /** Clear the session and return to the login screen. Fully functional today. */
  signOut: () => void;
  /**
   * DEMO role switching (SCRUM-387): swaps the ambient identity to the role's
   * demo user (v7 getCurrentUser) and persists the choice for the session.
   * Client-side ONLY — server-side authorization is unchanged until
   * SCRUM-102/103 (stated limitation, plan RT1).
   */
  setDemoRole: (role: DisplayRole) => void;
  /** Reset transient loading/error without changing auth. */
  clearError: () => void;
  /**
   * Development-only (ADR-014). Sets a session for a seeded user without any IdP
   * round trip. Only called from DevLoginScreen, which only renders behind
   * `import.meta.env.DEV`. Undefined in a production build: the property is only
   * added when `import.meta.env.DEV` is true, which Vite folds to `false` there, so
   * neither the action nor the sentinel token reaches the bundle (ADR-014 control 4).
   */
  signInDev?: (user: DevUser) => void;
}

/**
 * User-facing auth copy. PLACEHOLDER strings — the final wording (especially the
 * Pending-user flow) is defined when SCRUM-103 (Role-Based UI) and SCRUM-287
 * (Claim-to-Role Resolution / Pending-user state) land.
 */
export const AUTH_MESSAGES = {
  // Intentionally does NOT reveal whether the email or the tenant failed
  // (aligns with the SCRUM-102 acceptance criterion on not leaking
  // identity-level vs tenant-level failure).
  invalid: 'Invalid email or password.',
  pending: 'Your account is pending setup. Your bank admin has been notified.',
  notWired: 'Sign-in is being connected (SCRUM-102).',
  ssoUnavailable:
    'Single sign-on is unavailable right now. Please try again or contact your bank admin.',
  // Neutral, constant-shape (SCRUM-573, plan RT1): shown when resolve-tenant
  // returns null. Deliberately does NOT confirm or deny that the domain/tenant
  // exists — it only tells the user we can't route them and who to ask.
  unknownDomain:
    "We couldn't find a BankIQ workspace for that email address. Check the address or contact your bank administrator.",
  emailRequired: 'Enter your work email to continue.',
} as const;

/**
 * Builds the AuthUser for a role selected via the in-app role switcher
 * (SCRUM-387): the role's seeded identity when one exists (bank roles +
 * Platform Admin); for `Unassigned` the signed-in identity is kept — an
 * authenticated user whose admin hasn't granted a role. Used ONLY by the
 * `setDemoRole` action now that the demo sign-in shortcut is gone (SCRUM-576).
 */
function roleSwitchUser(role: DisplayRole, current: AuthUser | null): AuthUser {
  // SCRUM-643: the switcher swaps the VIEWED role but not the authenticated
  // session's tenant, so carry the current tenantId through — otherwise the
  // token-less surfaces (Vault download, SSE stream) would lose their tenant on
  // a role switch.
  const identity = DEMO_IDENTITIES[role];
  if (identity) {
    const base: AuthUser = {
      email: identity.email,
      displayName: identity.displayName,
      role,
    };
    const withBranch = identity.branch ? { ...base, branch: identity.branch } : base;
    return current?.tenantId ? { ...withBranch, tenantId: current.tenantId } : withBranch;
  }
  return {
    email: current?.email ?? 'unassigned@demo.bankiq',
    displayName: current?.displayName ?? 'New User',
    role,
    ...(current?.tenantId ? { tenantId: current.tenantId } : {}),
  };
}

/**
 * The real email-first sign-in (SCRUM-573, ADR-011 Flow-1), shared by signIn and
 * signInWithEntra: validate the email is present, resolve the tenant from its
 * domain, then begin the OIDC front-channel against that tenant's IdP (the page
 * navigates away on success — no terminal state update). An empty email, an
 * unknown domain, or a transport failure each set a neutral error and clear
 * loading. `set` is the store setter, passed in so this stays a free function.
 */
async function beginTenantSignIn(
  email: string,
  set: (partial: Partial<AuthState>) => void,
): Promise<void> {
  const trimmed = email.trim();
  if (trimmed.length === 0) {
    set({ error: AUTH_MESSAGES.emailRequired });
    return;
  }

  set({ isLoading: true, error: null });
  try {
    const { resolveTenant, beginEntraSignIn } = await import('./oidc');
    const tenantId = await resolveTenant(trimmed);
    if (tenantId === null) {
      // Constant-shape: we can't route this email. Neutral message; no signal
      // about whether the domain/tenant exists (plan RT1).
      set({ isLoading: false, error: AUTH_MESSAGES.unknownDomain });
      return;
    }
    await beginEntraSignIn(tenantId, trimmed);
    // No terminal state update on success — the browser is navigating to the IdP.
  } catch {
    set({ isLoading: false, error: AUTH_MESSAGES.ssoUnavailable });
  }
}

const initialState = {
  isAuthenticated: false,
  user: null as AuthUser | null,
  accessToken: null as string | null,
  isLoading: false,
  error: null as string | null,
};

export const useAuthStore = create<AuthState>((set, get) => ({
  ...initialState,

  signIn: async (email, password) => {
    /*
     * Email-first sign-in (SCRUM-573, ADR-011 Flow-1): the BankIQ page NEVER
     * collects the bank password — it resolves the user's tenant from their
     * email domain, then redirects to that tenant's bank IdP where the password
     * is entered. The `password` argument is retained on the interface only for
     * back-compat with callers/tests — the LoginPage no longer renders a
     * password field (SCRUM-576) and it is not used here. The demo sign-in
     * shortcut was removed in SCRUM-576 — every build runs the real
     * resolve-then-redirect path.
     */
    void password;
    await beginTenantSignIn(email, set);
  },

  signInWithEntra: async (email: string) => {
    /*
     * SSO button. There is no single "Entra" IdP — each bank federates its own,
     * so this defers to the same email-first flow as signIn (the LoginPage
     * passes the typed email through). The demo shortcut was removed in
     * SCRUM-576.
     */
    await beginTenantSignIn(email, set);
  },

  completeSsoCallback: async (callbackUrl: string) => {
    /*
     * SCRUM-102 Phase A: called from the /auth/callback route. Reads the
     * persisted signin state (PKCE verifier + nonce) and performs the
     * server-side code exchange; a success hydrates the store with the
     * enriched identity from the backend.
     */
    set({ isLoading: true, error: null });
    try {
      const { completeEntraSignIn } = await import('./oidc');
      const { user, accessToken } = await completeEntraSignIn(callbackUrl);
      set({ isLoading: false, isAuthenticated: true, user, accessToken });
    } catch (err) {
      set({
        isLoading: false,
        isAuthenticated: false,
        user: null,
        accessToken: null,
        error: err instanceof Error ? err.message : AUTH_MESSAGES.ssoUnavailable,
      });
    }
  },

  signOut: () => {
    // SCRUM-712: RP-initiated logout. Capture the tenant BEFORE clearing state,
    // then (a) clear the in-memory session immediately (no stale Bearer survives,
    // SCRUM-448) and (b) redirect to the IdP end-session endpoint so the IdP
    // session is terminated too — otherwise the IdP silently re-authenticates the
    // same user on the next login (CJ's report). Local state is cleared FIRST and
    // synchronously, so a slow/failed end-session lookup can never leave the user
    // stuck signed-in; buildSignOutUrl returns null (never throws) when the IdP
    // advertises no end-session endpoint or the config fetch fails, in which case
    // the SideNav's navigate('/login') fallback shows the login screen and the
    // SCRUM-713 prompt=login still forces a fresh credential prompt next time.
    const tenantId = get().user?.tenantId;
    set({ isAuthenticated: false, user: null, accessToken: null, error: null, isLoading: false });
    if (!tenantId) {
      return;
    }
    void import('./oidc').then(async ({ buildSignOutUrl }) => {
      const url = await buildSignOutUrl(tenantId);
      if (url) {
        window.location.assign(url);
      }
    });
  },

  setDemoRole: (role) => {
    persistDemoRole(role);
    set((state) => ({ user: roleSwitchUser(role, state.user) }));
  },

  clearError: () => {
    set({ error: null });
  },
  ...(import.meta.env.DEV
    ? {
        signInDev: (devUser: DevUser) => {
          set({
            isLoading: false,
            isAuthenticated: true,
            error: null,
            user: {
              email: devUser.email,
              displayName: devUser.displayName,
              // The store holds the display role, as the real token path does
              // (oidc.ts maps the role claim through toSessionRole). The stored
              // SystemRole name for X-Dev-Roles is looked up in api-client.
              role: toSessionRole(devUser.role),
            },
            accessToken: DEV_IMPERSONATION_TOKEN,
          });
        },
      }
    : {}),
}));

// Deliberately NOT persisted (no zustand/persist): (a) persisting demo auth
// would let a page refresh keep a fake session; (b) real session/token
// persistence is a SCRUM-102 security decision tied to token storage. A refresh
// returns the user to the login screen, which is correct and safe for the demo.
